import { useState, useEffect } from "react";

export default function KnowledgeBase() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // File list states
  const [files, setFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);

  const API_BASE = "https://zaichat.net/api";

  // Fetch files on component mount
  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_BASE}/knowledgebase/files`, {
        headers: { auth: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch files");
      const data = await response.json();
      setFiles(data.files || []);
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleDeleteFile = async (filename) => {
    // if (!alert(`Are you sure you want to delete "${filename}"? This will remove all associated data from your knowledge base.`)) return;

    setDeletingFile(filename);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication token not found. Please login again.");
        return;
      }

      const response = await fetch(
        `${API_BASE}/knowledgebase/files?filename=${filename}`,
        {
          method: "DELETE",
          headers: { auth: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete file");
      }

      const data = await response.json();
      
      // Show success message
      setUploadStatus({
        type: "success",
        message: `File "${filename}" deleted successfully! ${data.deleted_points || 0} points removed.`,
      });

      // Refresh file list
      await fetchFiles();

      // Clear success message after 3 seconds
      setTimeout(() => setUploadStatus(null), 3000);
    } catch (error) {
      setUploadStatus({
        type: "error",
        message: error.message || "Failed to delete file. Please try again.",
      });
      setTimeout(() => setUploadStatus(null), 5000);
    } finally {
      setDeletingFile(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const allowedTypes = ['.md', '.pdf', '.docx', '.txt'];
      const isValid = allowedTypes.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (isValid) {
        setSelectedFile(file);
        setUploadStatus(null);
      } else {
        setUploadStatus({
          type: "error",
          message: "Please select a valid file (.md, .pdf, .docx, or .txt)",
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setUploadStatus(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setUploadStatus({
          type: "error",
          message: "Authentication token not found. Please login again.",
        });
        setIsUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await fetch(`${API_BASE}/upload`, {
        method: "POST",
        headers: { auth: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      setUploadStatus({
        type: "success",
        message: `File uploaded successfully! ${data.chunks_uploaded} chunks created and added to your knowledge base.`,
      });

      // Refresh file list
      await fetchFiles();

      setTimeout(() => {
        setSelectedFile(null);
        setUploadStatus(null);
      }, 3000);
    } catch (error) {
      setUploadStatus({
        type: "error",
        message: "Failed to upload file. Please try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadStatus(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    const allowedTypes = ['.md', '.pdf', '.docx', '.txt'];
    const isValid = allowedTypes.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (file && isValid) {
      setSelectedFile(file);
      setUploadStatus(null);
    } else {
      setUploadStatus({
        type: "error",
        message: "Please select a valid file (.md, .pdf, .docx, or .txt)",
      });
    }
  };

  const getFileIcon = (filename) => {
    if (filename.toLowerCase().endsWith('.pdf')) {
      return '';
    } else if (filename.toLowerCase().endsWith('.docx')) {
      return '';
    } else if (filename.toLowerCase().endsWith('.md')) {
      return '';
    } else if (filename.toLowerCase().endsWith('.txt')) {
      return '';
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 text-gray-800 py-10 px-6 md:px-10">
      {/* Header with explanation */}
      <div className="max-w-4xl mx-auto mt-14 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h1 className="text-3xl font-bold text-gray-800">
            Knowledge Base Manager
          </h1>
        </div>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">How it works</h3>
              <p className="text-blue-800 text-sm leading-relaxed">
                Upload your documents to train your AI assistant. The system will automatically extract text, break it into chunks, 
                and store it in your knowledge base. Your chatbot will then be able to answer questions based on this information.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Base Feeder Section */}
      <div className="mb-12 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800">
            Upload Documents
          </h2>
        </div>

        <p className="text-gray-600 mb-4">
          Upload your documents to expand your AI's knowledge. We support multiple formats:
        </p>
        
        {/* Supported formats badges */}
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-200 rounded-full text-sm font-medium text-gray-700">
             <span className="text-orange-600">Markdown</span> (.md)
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-200 rounded-full text-sm font-medium text-gray-700">
             <span className="text-orange-600">PDF</span> (.pdf)
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-200 rounded-full text-sm font-medium text-gray-700">
             <span className="text-orange-600">Word</span> (.docx)
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-200 rounded-full text-sm font-medium text-gray-700">
             <span className="text-orange-600">Text</span> (.txt)
          </span>
        </div>

        <div className="bg-white border border-orange-200 rounded-2xl shadow-lg p-8">
          {!selectedFile ? (
            <div
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${
                dragActive
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-300 hover:border-orange-500 hover:bg-orange-50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById("fileInput").click()}
            >
              <svg className="w-16 h-16 text-orange-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">
                Drag & drop your file here
              </h4>
              <p className="text-gray-500 text-sm mb-4">or click to browse from your computer</p>
              <div className="inline-block px-4 py-2 bg-orange-50 rounded-lg text-xs text-gray-600">
                Supports: .md, .pdf, .docx, .txt
              </div>
              <input
                type="file"
                id="fileInput"
                className="hidden"
                onChange={handleFileSelect}
                accept=".md,.pdf,.docx,.txt"
              />
            </div>
          ) : (
            <div className="text-center bg-orange-50 rounded-2xl p-8">
              <div className="text-6xl mb-4">{getFileIcon(selectedFile.name)}</div>
              <h5 className="text-lg font-semibold text-gray-800 mb-1">
                {selectedFile.name}
              </h5>
              <p className="text-gray-600 text-sm mb-2">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
              <p className="text-gray-500 text-xs mb-6">
                Ready to be processed and added to your knowledge base
              </p>

              <div className="flex justify-center gap-4">
                <button
                  className="px-5 py-2 rounded-full border border-red-400 text-red-500 font-medium hover:bg-red-50 transition"
                  onClick={handleRemoveFile}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2 rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 transition disabled:opacity-50 flex items-center gap-2"
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload to Knowledge Base
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {uploadStatus && (
            <div
              className={`mt-5 p-4 rounded-lg text-sm font-medium flex items-center gap-2 ${
                uploadStatus.type === "success"
                  ? "bg-green-100 text-green-800 border border-green-300"
                  : "bg-red-100 text-red-700 border border-red-300"
              }`}
            >
              {uploadStatus.type === "success" ? "✅" : "❌"}
              <span>{uploadStatus.message}</span>
            </div>
          )}
        </div>
      </div>

      {/* Uploaded Files Section */}
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
            </svg>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Your Knowledge Base
              </h2>
              <p className="text-sm text-gray-500">
                {files.length} {files.length === 1 ? 'document' : 'documents'} in your knowledge base
              </p>
            </div>
          </div>
          <button
            onClick={fetchFiles}
            disabled={isLoadingFiles}
            className="p-2 rounded-full hover:bg-orange-100 transition disabled:opacity-50"
            title="Refresh list"
          >
            <svg className={`w-5 h-5 text-orange-500 ${isLoadingFiles ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>
        </div>

        <div className="bg-white border border-orange-200 rounded-2xl shadow-lg p-6">
          {isLoadingFiles ? (
            <div className="text-center py-12">
              <svg className="w-8 h-8 text-orange-500 mx-auto mb-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-500">Loading your documents...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 font-medium mb-1">Your knowledge base is empty</p>
              <p className="text-gray-400 text-sm">
                Upload your first document above to get started
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {files.map((filename) => (
                <div
                  key={filename}
                  className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-100 hover:bg-orange-100 transition group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-2xl flex-shrink-0">{getFileIcon(filename)}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-800 block truncate">
                        {filename}
                      </span>
                      <span className="text-xs text-gray-500">
                        Document in knowledge base
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteFile(filename)}
                    disabled={deletingFile === filename}
                    className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition disabled:opacity-50 flex-shrink-0 ml-3"
                    title="Remove from knowledge base"
                  >
                    {deletingFile === filename ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}